import Link from 'next/link';
import Logo from './Logo';
import StatusBar from './StatusBar';
import { NPM_PACKAGE_URL } from '@/lib/site';
const COMPLIANCE_BADGES = [
  { mark: 'GDPR', label: 'GDPR', status: 'In progress' },
  { mark: 'SOC 2', label: 'SOC 2', status: 'In progress' },
  { mark: 'ISO', label: 'ISO 27001', status: 'Compliant' },
];

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

const COLUMNS: { title: string; links: FooterLink[] }[] = [
  {
    title: 'Product',
    links: [
      { label: 'Features', href: '/#features' },
      { label: 'Pricing', href: '/#pricing' },
      { label: 'Silker Shield', href: '/shield' },
      { label: 'Mobile Security', href: '/mobile' },
      { label: 'Web3 Tools', href: '/web3' },
      { label: 'For Product Hunt', href: '/product-hunt' },
      { label: 'Why Silker', href: '/why-us' },
      { label: 'Silker vs alternatives', href: '/compare' },
      { label: 'Architecture', href: '/architecture' },
      { label: 'Benefits', href: '/benefits' },
      { label: 'Roadmap', href: '/roadmap' },
    ],
  },
  {
    title: 'Developers',
    links: [
      { label: 'Documentation', href: '/docs' },
      { label: 'Quickstart', href: '/docs/quickstart' },
      { label: 'API security guide', href: '/guides/how-to-secure-an-api' },
      { label: 'Reverse proxy guide', href: '/guides/what-is-a-reverse-proxy' },
      { label: 'Benchmarks', href: '/benchmarks' },
      { label: 'Changelog', href: '/changelog' },
      { label: 'npm package', href: NPM_PACKAGE_URL, external: true },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'Case Studies', href: '/case-study' },
      { label: 'Best WAF', href: '/best-waf' },
      { label: 'Cloudflare WAF alternative', href: '/vs/cloudflare-waf' },
      { label: 'FAQ', href: '/faq' },
      { label: 'Investors', href: '/investors' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms', href: '/terms' },
      { label: 'Privacy', href: '/privacy' },
      { label: 'Security', href: '/security' },
      { label: 'DPA', href: '/dpa' },
    ],
  },
];

function FooterLinkItem({ link }: { link: FooterLink }) {
  const className = 'text-sm text-neutral-400 hover:text-white transition cursor-pointer';
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
        {link.label}
      </a>
    );
  }
  return (
    <Link href={link.href} className={className}>
      {link.label}
    </Link>
  );
}

export default function Footer() {
  return (
    <footer className="w-full border-t border-white/10 mt-auto">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Top: brand + link columns */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-8">
          <div className="col-span-2">
            <Link href="/" className="inline-flex items-center gap-2 text-lg font-semibold text-white">
              <Logo className="w-7 h-7 rounded-lg" />
              <span className="bg-gradient-to-r from-white via-rose-200 to-white bg-clip-text text-transparent">
                Silker AI
              </span>
            </Link>
            <p className="mt-3 text-sm text-neutral-500 max-w-xs leading-relaxed">
              Runtime security for AI-built apps. Block prompt injection, data leaks and OWASP threats - one npm install.
            </p>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title} className="flex flex-col gap-2.5">
              <span className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">{col.title}</span>
              {col.links.map((link) => (
                <FooterLinkItem key={link.label} link={link} />
              ))}
            </div>
          ))}
        </div>

        {/* Status bar */}
        <div className="mt-8 pt-6 border-t border-white/10 flex justify-center">
          <StatusBar />
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {COMPLIANCE_BADGES.map((badge) => (
            <span key={badge.label} className="inline-flex items-center gap-2 rounded-none border border-white/10 bg-white/[0.03] px-3 py-2 text-left shadow-[0_0_24px_rgba(255,255,255,0.03)]">
              <span className="flex h-8 min-w-12 items-center justify-center rounded-none border border-emerald-400/25 bg-emerald-400/10 px-2 font-mono text-[10px] font-semibold tracking-tight text-emerald-200">
                {badge.mark}
              </span>
              <span className="flex flex-col leading-none">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-200">
                  {badge.label}
                </span>
                <span className="mt-1 text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                  {badge.status}
                </span>
              </span>
            </span>
          ))}
        </div>

        {/* Bottom bar: copyright left · Made in Poland right */}
        <div className="mt-10 pt-6 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
          <div className="text-xs text-neutral-500 text-center sm:text-left order-2 sm:order-1">
            © 2026 Silker AI. All rights reserved.
          </div>

          <div className="flex justify-center sm:justify-end items-center gap-3 order-1 sm:order-2">
            <a href="https://saasbrowser.com/en/saas/1452524/silker-ai" target="_blank" rel="noopener" className="opacity-70 hover:opacity-100 transition-opacity">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="https://static-files.saasbrowser.com/saas-browser-badge-15.svg" alt="Silker AI - SaaS database" className="h-9 w-auto" />
            </a>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-none bg-white/5 border border-white/10">
              <span className="text-xs text-neutral-300">Made in Poland</span>
              <span className="text-base">🇵🇱</span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
